import env from '@repo/env';
import { StoreSelector } from '@src/types';
import Cookies from 'js-cookie';
import { create } from 'zustand';

import { Theme } from './constants';

export interface ThemeState {
  theme: Theme;
  themeDisabled: boolean;
  setThemeDisabled: (disabled: boolean) => void;
  setTheme: (theme: Theme) => void;
  setForcedTheme: (theme: Theme) => void;
  toggleTheme: () => Theme;
  isNewThemeActive: boolean;
}

const THEME_KEY = 'htcTheme';

const getInitialTheme = (): Theme => {
  const themeValue = Cookies.get(THEME_KEY);
  if (themeValue === Theme.DARK) {
    return Theme.DARK;
  }
  return Theme.LIGHT;
};

export const useThemeStore = create<ThemeState>((set, get) => ({
  themeDisabled: false,
  isNewThemeActive: true,

  theme: getInitialTheme(),

  setTheme: (theme): void => {
    if (get().themeDisabled) {
      return;
    }

    set(() => ({ theme }));
    Cookies.set(THEME_KEY, theme, {
      path: '/',
      expires: 365,
      domain: env.cookieDomain,
      sameSite: 'lax',
      secure: true,
    });
  },

  setForcedTheme: (theme): void => {
    set(() => ({ theme }));
  },

  toggleTheme: (): Theme => {
    const { theme, setTheme } = get();
    const newTheme = theme === Theme.LIGHT ? Theme.DARK : Theme.LIGHT;
    setTheme(newTheme);
    return newTheme;
  },

  setThemeDisabled: (disabled): void => {
    set({ themeDisabled: disabled });
  },
}));

export const themeStoreSelector: StoreSelector<ThemeState, keyof ThemeState> = {
  themeDisabled: (state) => state.themeDisabled,
  theme: (state) => state.theme,
  setTheme: (state) => state.setTheme,
  setForcedTheme: (state) => state.setForcedTheme,
  toggleTheme: (state) => state.toggleTheme,
  setThemeDisabled: (state) => state.setThemeDisabled,
  isNewThemeActive: (state) => state.isNewThemeActive,
};
